import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Avatar,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  PushPin as PinIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { useAuth, useClubs } from '../context/AppContext';
import DiscussionService, { DiscussionThread } from '../services/discussionService';

interface MessageProps {
  message: {
    id: string;
    authorId: string;
    authorName: string;
    authorRole: string;
    authorProfilePicture?: string;
    content: string;
    timestamp: string;
    upvotes?: number;
    downvotes?: number;
    netVotes?: number;
    userVote?: 'upvote' | 'downvote' | null;
  };
  isCurrentUser: boolean;
  showAuthor: boolean;
  onAuthorPress: (authorId: string) => void;
  onReply: (messageId: string, authorName: string) => void;
  onVote?: (messageId: string, voteType: 'upvote' | 'downvote') => void;
}

const MessageComponent: React.FC<MessageProps> = ({
  message,
  isCurrentUser,
  showAuthor,
  onAuthorPress,
  onReply,
  onVote,
}) => {
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = (now.getTime() - date.getTime()) / (1000 * 60);
    const diffInHours = diffInMinutes / 60;
    const diffInDays = diffInHours / 24;

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${Math.floor(diffInMinutes)}m ago`;
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    if (diffInDays < 7) return `${Math.floor(diffInDays)}d ago`;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAuthorColor = (authorName: string) => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
    let hash = 0;
    for (let i = 0; i < authorName.length; i++) {
      hash = authorName.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <Card sx={{
      mb: { xs: 1.5, sm: 2 },
      mx: { xs: 1.5, sm: 3 },
      border: '1px solid #e4e6ea',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      backgroundColor: isCurrentUser ? '#f8f9fa' : 'white',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      <CardContent sx={{ p: 0 }}>
        {/* Forum-style header */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: { xs: 1.5, sm: 3 },
          backgroundColor: isCurrentUser ? '#e3f2fd' : '#f8f9fa',
          borderBottom: '1px solid #e4e6ea',
          gap: { xs: 1, sm: 2 }
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.75, sm: 1.5 },
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
            flex: 1,
            minWidth: 0,
            overflow: 'hidden'
          }} onClick={() => onAuthorPress(message.authorId)}>
            <Avatar sx={{
              width: { xs: 28, sm: 36 },
              height: { xs: 28, sm: 36 },
              bgcolor: getAuthorColor(message.authorName),
              fontSize: { xs: '0.8rem', sm: '1rem' },
              fontWeight: 600,
              flexShrink: 0
            }}>
              {message.authorName.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
              <Typography variant="body1" sx={{
                fontWeight: 600,
                fontSize: { xs: '0.85rem', sm: '1rem' },
                color: isCurrentUser ? '#1976d2' : '#2e2e2e',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {message.authorName} {isCurrentUser && '(You)'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{
                fontSize: { xs: '0.7rem', sm: '0.8rem' }
              }}>
                {formatTime(message.timestamp)}
              </Typography>
            </Box>
          </Box>

          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              onReply(message.id, message.authorName);
            }}
            sx={{
              textTransform: 'none',
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
              minWidth: 'auto',
              px: { xs: 0.75, sm: 1.5 },
              py: { xs: 0.25, sm: 0.5 },
              borderColor: '#e4e6ea',
              color: '#666',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: '#f0f2f5',
                borderColor: '#d0d3d8'
              }
            }}
          >
            Reply
          </Button>
        </Box>

        {/* Forum-style message content with voting */}
        <Box sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          p: { xs: 1.5, sm: 3 },
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          {/* Voting Section */}
          {onVote && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: { xs: 0.25, sm: 0.5 },
              minWidth: { xs: '32px', sm: '40px' }
            }}>
              <IconButton
                size="small"
                onClick={() => onVote(message.id, 'upvote')}
                sx={{
                  color: message.userVote === 'upvote' ? '#FF8B60' : '#8E8E93',
                  '&:hover': {
                    backgroundColor: '#FF8B6020',
                    color: '#FF8B60'
                  },
                  p: { xs: 0.5, sm: 0.75 }
                }}
              >
                <ArrowUpwardIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem' } }} />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  color: message.netVotes && message.netVotes > 0 ? '#FF8B60' :
                         message.netVotes && message.netVotes < 0 ? '#5856D6' : '#8E8E93',
                  minWidth: { xs: '24px', sm: '28px' },
                  textAlign: 'center'
                }}
              >
                {message.netVotes || 0}
              </Typography>
              <IconButton
                size="small"
                onClick={() => onVote(message.id, 'downvote')}
                sx={{
                  color: message.userVote === 'downvote' ? '#5856D6' : '#8E8E93',
                  '&:hover': {
                    backgroundColor: '#5856D620',
                    color: '#5856D6'
                  },
                  p: { xs: 0.5, sm: 0.75 }
                }}
              >
                <ArrowDownwardIcon sx={{ fontSize: { xs: '1.2rem', sm: '1.4rem' } }} />
              </IconButton>
            </Box>
          )}
          {/* Message Content */}
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <Typography
              variant="body1"
              sx={{
                color: '#2e2e2e',
                fontSize: { xs: '0.85rem', sm: '1rem' },
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }}
            >
              {message.content}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const DiscussionThreadPage: React.FC = () => {
  const { clubId, discussionId } = useParams<{ clubId: string; discussionId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { clubs } = useClubs();

  const [discussion, setDiscussion] = useState<DiscussionThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [newReply, setNewReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState<{id: string, author: string} | null>(null);
  const [error, setError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const club = clubs.find(c => c.id === clubId);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadDiscussion = useCallback(async () => {
    if (!discussionId) return;

    try {
      setLoading(true);
      setError(null);
      const discussionData = await DiscussionService.getDiscussionThread(discussionId);
      setDiscussion(discussionData);
    } catch (error: any) {
      console.error('Error loading discussion:', error);
      setError('Failed to load discussion');
    } finally {
      setLoading(false);
    }
  }, [discussionId]);

  useEffect(() => {
    loadDiscussion();
  }, [loadDiscussion]);

  useEffect(() => {
    if (discussion && discussion.replies.length > 0) {
      scrollToBottom();
    }
  }, [discussion?.replies.length]);

  const handleSendReply = async () => {
    if (!newReply.trim() || sending || !discussionId) return;

    const replyContent = newReply.trim();
    const finalContent = replyingTo
      ? `@${replyingTo.author} ${replyContent}`
      : replyContent;

    setNewReply('');
    setReplyingTo(null);
    setSending(true);

    try {
      await DiscussionService.replyToDiscussion(discussionId, { content: finalContent });

      // Reload discussion to get updated replies
      await loadDiscussion();

      // Scroll to bottom after a short delay to ensure new message is rendered
      setTimeout(scrollToBottom, 100);
    } catch (error: any) {
      console.error('Error sending reply:', error);
      setError('Failed to send reply. Please try again.');
      setNewReply(replyContent);
    } finally {
      setSending(false);
    }
  };

  const handleAuthorPress = (authorId: string) => {
    // TODO: Navigate to user profile
    console.log('Navigate to user profile:', authorId);
  };

  const handleReply = (messageId: string, authorName: string) => {
    setReplyingTo({ id: messageId, author: authorName });
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  const handleVote = async (replyId: string, voteType: 'upvote' | 'downvote') => {
    if (!discussionId || !discussion) return;

    try {
      // Call the API
      const voteData = voteType === 'upvote'
        ? await DiscussionService.upvoteReply(discussionId, replyId)
        : await DiscussionService.downvoteReply(discussionId, replyId);

      // Update the local state with the new vote data
      setDiscussion(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          replies: prev.replies.map(reply =>
            reply.id === replyId
              ? {
                  ...reply,
                  upvotes: voteData.upvotes,
                  downvotes: voteData.downvotes,
                  netVotes: voteData.netVotes,
                  userVote: voteData.userVote
                }
              : reply
          )
        };
      });
    } catch (error: any) {
      console.error('Error voting on reply:', error);
      setError('Failed to vote. Please try again.');
      // Clear error after 3 seconds
      setTimeout(() => setError(null), 3000);
    }
  };

  const renderDiscussionHeader = () => {
    if (!discussion) return null;

    const getCategoryColor = (category: string) => {
      switch (category) {
        case 'general': return '#007AFF';
        case 'technical': return '#5856D6';
        case 'events': return '#FF9500';
        case 'support': return '#34C759';
        default: return '#8E8E93';
      }
    };

    const formatDate = (timestamp: string) => {
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    return (
      <Card sx={{
        mb: { xs: 1, sm: 2 },
        mx: { xs: 0, sm: 3 },
        borderRadius: { xs: 0, sm: 2 },
        border: { xs: 'none', sm: '1px solid #e4e6ea' },
        borderBottom: { xs: '1px solid #e4e6ea', sm: 'none' },
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {discussion.isPinned && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              p: 1,
              bgcolor: '#fff3cd',
              borderRadius: 1,
              border: '1px solid #ffeaa7'
            }}>
              <PinIcon sx={{ fontSize: 16, mr: 1, color: '#856404' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#856404' }}>
                📌 Pinned Discussion
              </Typography>
            </Box>
          )}

          <Chip
            label={`#${discussion.category.toUpperCase()}`}
            size="small"
            sx={{
              bgcolor: getCategoryColor(discussion.category) + '20',
              color: getCategoryColor(discussion.category),
              fontWeight: 700,
              fontSize: '0.7rem',
              mb: 2
            }}
          />

          <Typography variant="h5" sx={{
            fontWeight: 700,
            mb: 2,
            color: '#1877f2',
            fontSize: { xs: '1.3rem', sm: '1.5rem' }
          }}>
            {discussion.title}
          </Typography>

          {discussion.content && (
            <Card sx={{
              bgcolor: '#f8f9fa',
              mb: 2,
              borderLeft: '4px solid #1877f2'
            }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="body1" sx={{
                  color: '#3c3c43',
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap'
                }}>
                  {discussion.content}
                </Typography>
              </CardContent>
            </Card>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
               onClick={() => handleAuthorPress(discussion.authorId)}>
            <Avatar sx={{
              width: 36,
              height: 36,
              bgcolor: '#34c759',
              mr: 1.5,
              fontSize: '0.8rem',
              fontWeight: 700
            }}>
              OP
            </Avatar>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {discussion.authorName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                {formatDate(discussion.timestamp)}
              </Typography>
            </Box>
          </Box>

          {discussion.replies.length > 0 && (
            <Box sx={{ textAlign: 'center', mt: 2, pt: 2, borderTop: '1px solid #f3f4f6' }}>
              <Typography variant="body2" sx={{
                color: '#1877f2',
                fontWeight: 600
              }}>
                {discussion.replies.length} {discussion.replies.length === 1 ? 'reply' : 'replies'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'white',
          borderBottom: '1px solid #e4e6ea',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <IconButton onClick={() => navigate(`/clubs/${clubId}/discussions`)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Loading Discussion...
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error || !discussion) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'white',
          borderBottom: '1px solid #e4e6ea',
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <IconButton onClick={() => navigate(`/clubs/${clubId}/discussions`)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Discussion
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', px: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Discussion not found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
            This discussion may have been deleted or you don't have access to it.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(`/clubs/${clubId}/discussions`)}
            sx={{ textTransform: 'none' }}
          >
            Go Back
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: { xs: 'white', sm: '#f0f2f5' },
      pb: { xs: 20, sm: 10 },
      overflow: 'hidden',
      maxWidth: '100vw'
    }}>
      {/* Header */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backgroundColor: 'white',
        borderBottom: '1px solid #e4e6ea',
        px: { xs: 2, sm: 3 },
        py: { xs: 1.5, sm: 2 },
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 2 },
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '100%'
      }}>
        <IconButton
          onClick={() => navigate(`/clubs/${clubId}/discussions`)}
          size="small"
          sx={{
            backgroundColor: '#f0f2f5',
            p: { xs: 0.75, sm: 1.2 },
            flexShrink: 0,
            '&:hover': {
              backgroundColor: '#e4e6ea'
            }
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <Typography variant="h6" sx={{
            fontWeight: 600,
            fontSize: { xs: '0.95rem', sm: '1.25rem' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {club?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{
            fontSize: { xs: '0.7rem', sm: '0.85rem' }
          }}>
            Discussion
          </Typography>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{
        maxWidth: { xs: '100%', sm: 800 },
        mx: 'auto',
        pt: { xs: 0, sm: 2 },
        width: '100%',
        overflow: 'hidden'
      }}>
        {error && (
          <Alert severity="error" sx={{
            mx: { xs: 2, sm: 3 },
            mb: { xs: 1, sm: 2 },
            borderRadius: { xs: 0, sm: 1 }
          }}>
            {error}
          </Alert>
        )}

        {/* Discussion Header */}
        {renderDiscussionHeader()}

        {/* Messages */}
        <Box sx={{ pb: 2 }}>
          {discussion.replies.length > 0 ? (
            // Sort replies chronologically (oldest to newest) to ensure latest at bottom
            discussion.replies
              .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
              .map((reply) => {
                const isCurrentUser = reply.authorId === user?.id;

                return (
                  <MessageComponent
                    key={reply.id}
                    message={reply}
                    isCurrentUser={isCurrentUser}
                    showAuthor={true} // Always show author in forum style
                    onAuthorPress={handleAuthorPress}
                    onReply={handleReply}
                    onVote={handleVote}
                  />
                );
              })
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h1" sx={{ fontSize: '3rem', mb: 2 }}>💬</Typography>
              <Typography variant="body1" color="text.secondary">
                Be the first to reply to this discussion!
              </Typography>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Box>
      </Box>

      {/* Reply Input - Fixed at bottom */}
      <Box sx={{
        position: 'fixed',
        bottom: { xs: 56, sm: 0 },
        left: 0,
        right: 0,
        bgcolor: 'white',
        borderTop: '1px solid #e4e6ea',
        p: { xs: 1.5, sm: 3 },
        zIndex: 1000,
        boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
        maxWidth: '100vw',
        overflow: 'hidden'
      }}>
        <Box sx={{
          maxWidth: { xs: '100%', sm: 800 },
          mx: 'auto',
          width: '100%'
        }}>
          {replyingTo && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: '#f8f9fa',
              p: { xs: 1, sm: 1.5 },
              borderRadius: { xs: 1, sm: 1.5 },
              mb: { xs: 1, sm: 2 }
            }}>
              <Typography variant="body2" color="text.secondary" sx={{
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              }}>
                Replying to <Typography component="span" sx={{
                  fontWeight: 600,
                  color: '#1877f2'
                }}>
                  @{replyingTo.author}
                </Typography>
              </Typography>
              <IconButton size="small" onClick={handleCancelReply} sx={{
                p: { xs: 0.5, sm: 1 }
              }}>
                <Typography sx={{
                  fontSize: { xs: '0.9rem', sm: '1rem' }
                }}>✕</Typography>
              </IconButton>
            </Box>
          )}

          <Box sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: { xs: 0.75, sm: 1.5 },
            width: '100%',
            maxWidth: '100%'
          }}>
            <Avatar sx={{
              width: { xs: 28, sm: 32 },
              height: { xs: 28, sm: 32 },
              bgcolor: '#1877f2',
              fontSize: { xs: '0.8rem', sm: '0.9rem' },
              fontWeight: 600,
              flexShrink: 0
            }}>
              {user?.name?.charAt(0).toUpperCase() || '👤'}
            </Avatar>

            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type your reply..."
              value={newReply}
              onChange={(e) => setNewReply(e.target.value)}
              disabled={sending}
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  borderRadius: { xs: 2.5, sm: 3 },
                  bgcolor: '#f8f9fa',
                  fontSize: { xs: '0.85rem', sm: '1rem' },
                  minHeight: { xs: 40, sm: 'auto' }
                }
              }}
            />

            <IconButton
              color="primary"
              onClick={handleSendReply}
              disabled={!newReply.trim() || sending}
              sx={{
                bgcolor: '#1877f2',
                color: 'white',
                width: { xs: 34, sm: 40 },
                height: { xs: 34, sm: 40 },
                flexShrink: 0,
                '&:hover': { bgcolor: '#166fe5' },
                '&:disabled': { bgcolor: '#c7c7cc', color: '#8e8e93' }
              }}
            >
              {sending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DiscussionThreadPage;